import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import type { UpdateCampaignApiArg } from '@/__generated/es-api'
import { clientRef } from '@/app/client-ref'
import { ERROR_SAVE_CAMPAIGN_SERVER } from '@/app/module/campaigns/definitions'
import type { CampaignSaveData } from '@/app/module/campaigns/types/save-data'
import { toCampaignUpdateBody } from '@/app/module/campaigns/utils/campaign-payload'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

// Used by the chatbot editor and dashboard renaming.
export const useUpdateCampaign = () => {
  const orgId = useSelector(selectOrgId)
  const dispatch = useAppDispatch()
  const [update, result] = esApi.endpoints.updateCampaign.useMutation()

  const updateCampaign = useCallback(
    async (campaignId: number | string, body: CampaignSaveData<UpdateCampaignApiArg['body']>) => {
      try {
        const res = await update({
          orgId: orgId.toString(),
          campaignId: String(campaignId),
          body: { ...toCampaignUpdateBody(body), clientRef },
        }).unwrap()
        dispatch(
          createNotification({
            'update-campaign-success': {
              type: 'success',
              message: 'Campaign saved successfully.',
            },
          }),
        )
        return res
      } catch (error) {
        dispatch(
          createNotification({
            'update-campaign-failure': {
              type: 'error',
              message: getErrorMessage(error, ERROR_SAVE_CAMPAIGN_SERVER),
            },
          }),
        )
        return undefined
      }
    },
    [update, dispatch, orgId],
  )

  return [updateCampaign, result] as const
}
